/**
 * Playbooks — Pre-built workflow template gallery (table format)
 *
 * Features:
 * - Table of 7 playbook templates with rich multi-line rows
 * - Category filter (All/Maintenance/Safety/Compliance/Operations)
 * - Search by name or tags
 * - Columns: Playbook (icon+name+desc+tags), Category, Agent, Steps, Duration, Success, Actions
 * - Navigate to detail page or create automation from template
 */

import { createFileRoute, useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Search, BookOpen, Clock, Plus, Loader2,
  Layers, Tag, Sparkles, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { AgentIcon } from '@/components/shared/icon-picker';
import { cn } from '@/lib/utils';
import {
  PLAYBOOK_CATEGORY_STYLES,
  type PlaybookCategory,
} from '@/lib/mock-automate-data';
import { usePlaybooks, useInstantiatePlaybook } from '@/lib/use-automate';

export const Route = createFileRoute('/automate/playbooks')({
  component: PlaybooksPage,
});

const CATEGORY_OPTIONS: Array<{ value: 'all' | PlaybookCategory; label: string }> = [
  { value: 'all', label: 'All Categories' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'safety', label: 'Safety' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'operations', label: 'Operations' },
  { value: 'documentation', label: 'Documentation' },
];

function PlaybooksPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | PlaybookCategory>('all');

  // ⚠ #169 — every hook below MUST run on every render. The early
  // `return <Outlet/>` for child routes used to live above the data
  // hooks; navigating between list <-> detail then changed the hook
  // count and crashed React with error #300 ("Rendered fewer hooks
  // than expected"). Keep the route-discrimination return at the end
  // of the function, after all hooks.
  const { data: playbooks = [] } = usePlaybooks();
  const instantiate = useInstantiatePlaybook();
  const [usingPlaybookId, setUsingPlaybookId] = useState<string | null>(null);

  const handleUsePlaybook = (playbookId: string, playbookName: string) => {
    if (instantiate.isPending) return;
    setUsingPlaybookId(playbookId);
    instantiate.mutate(
      { playbookId },
      {
        onSuccess: ({ id }) => {
          setUsingPlaybookId(null);
          toast.success('Workflow created from playbook', {
            description: `Opening "${playbookName}" in the builder…`,
          });
          navigate({ to: '/automate/workflows/$id', params: { id } } as never);
        },
        onError: (err) => {
          setUsingPlaybookId(null);
          toast.error('Could not create workflow from playbook', {
            description: err.message ?? 'Server returned an error.',
          });
        },
      },
    );
  };

  const filtered = useMemo(() => {
    return playbooks.filter(pb => {
      if (categoryFilter !== 'all' && pb.category !== categoryFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        pb.name.toLowerCase().includes(q) ||
        pb.description.toLowerCase().includes(q) ||
        pb.tags.some(t => t.toLowerCase().includes(q))
      );
    });
  }, [playbooks, search, categoryFilter]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'name');

  // Now that all hooks have run, it's safe to short-circuit when the
  // route discriminator says "render the child detail, not the list".
  const isIndex =
    location.pathname === '/automate/playbooks' ||
    location.pathname === '/automate/playbooks/';
  if (!isIndex) return <Outlet />;

  const total = playbooks.length;
  const categories = new Set(playbooks.map(p => p.category)).size;
  // Backend stores success_rate as 0..100 (percent). Clamp + average in
  // 0..100 space; display as percent. Issue #100.
  const _scored = playbooks.filter(p => p.successRate > 0);
  const avgSuccessPct = _scored.reduce((sum, p) => sum + Math.min(100, Math.max(0, p.successRate)), 0) / Math.max(_scored.length, 1);
  const totalSteps = playbooks.reduce((sum, p) => sum + p.steps.length, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold">{total}</p><p className="text-xs text-muted-foreground">Templates</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{categories}</p><p className="text-xs text-muted-foreground">Categories</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{Math.round(avgSuccessPct)}%</p><p className="text-xs text-muted-foreground">Avg Success</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold">{totalSteps}</p><p className="text-xs text-muted-foreground">Total Steps</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search playbooks by name or tags..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-1.5">
          {CATEGORY_OPTIONS.map(opt => (
            <Button
              key={opt.value}
              variant={categoryFilter === opt.value ? 'default' : 'outline'}
              size="sm"
              className="text-xs h-8"
              onClick={() => setCategoryFilter(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {filtered.length} template{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Playbook Table */}
      {sorted.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header */}
            <div className="grid grid-cols-[minmax(320px,2fr)_100px_140px_70px_90px_80px_110px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider">
              <SortableHeader label="Playbook" sortKey="name" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Category" sortKey="category" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Agent" sortKey="agentName" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Steps" sortKey="steps.length" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Duration" sortKey="estimatedDuration" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Success" sortKey="successRate" currentSort={sortConfig} onSort={handleSort} />
              <span className="text-muted-foreground font-semibold">Actions</span>
            </div>
            {/* Rows */}
            <div className="divide-y">
              {sorted.map(pb => {
                const catStyle = PLAYBOOK_CATEGORY_STYLES[pb.category];
                return (
                  <div
                    key={pb.id}
                    className="grid grid-cols-[minmax(320px,2fr)_100px_140px_70px_90px_80px_110px] gap-3 px-4 py-3 items-center hover:bg-muted/30 transition-colors cursor-pointer group"
                    onClick={() => navigate({ to: '/automate/playbooks/$playbookId', params: { playbookId: pb.id } })}
                  >
                    {/* Playbook name + description + tags */}
                    <div className="flex items-center gap-3 min-w-0">
                      <AgentIcon iconName={pb.agentIcon} colorName={pb.agentIconColor} size="sm" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{pb.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{pb.description}</p>
                        {pb.tags.length > 0 && (
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                            {pb.tags.slice(0, 4).map(tag => (
                              <span key={tag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                                <Tag className="h-2 w-2" />
                                {tag}
                              </span>
                            ))}
                            {pb.tags.length > 4 && (
                              <span className="text-[10px] text-muted-foreground">+{pb.tags.length - 4}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Category */}
                    <Badge variant="outline" className={cn('text-[10px] w-fit', catStyle.color)}>
                      {catStyle.label}
                    </Badge>

                    {/* Agent */}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                      <span className="truncate">{pb.agentName}</span>
                    </div>

                    {/* Steps */}
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Layers className="h-3.5 w-3.5" />
                      <span>{pb.steps.length}</span>
                    </div>

                    {/* Duration */}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      <span>{pb.estimatedDuration}</span>
                    </div>

                    {/* Success Rate — backend stores 0..100; clamp + display as % (#100) */}
                    {pb.successRate > 0 ? (() => {
                      const clamped = Math.min(100, Math.max(0, pb.successRate));
                      return (
                        <span className={cn('text-sm font-semibold', clamped >= 90 ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300')}>
                          {Math.round(clamped)}%
                        </span>
                      );
                    })() : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        title="View playbook details"
                        onClick={() => navigate({ to: '/automate/playbooks/$playbookId', params: { playbookId: pb.id } })}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        disabled={usingPlaybookId === pb.id}
                        className="text-xs h-7 gap-1 px-2.5"
                        title="Create a new workflow from this playbook"
                        onClick={() => handleUsePlaybook(pb.id, pb.name)}
                      >
                        {usingPlaybookId === pb.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Plus className="h-3 w-3" />
                        )}
                        {usingPlaybookId === pb.id ? 'Creating…' : 'Use'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : playbooks.length === 0 ? (
        <div className="border border-dashed rounded-lg p-12 flex flex-col items-center justify-center text-center">
          <BookOpen className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No playbooks defined for this tenant</p>
          <p className="text-xs text-muted-foreground mt-1">
            Playbooks are reusable agent procedures. They are typically provisioned by the platform admin.
          </p>
        </div>
      ) : (
        <div className="border border-dashed rounded-lg p-12 flex flex-col items-center justify-center text-center">
          <BookOpen className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No playbooks match your filters</p>
          <p className="text-xs text-muted-foreground mt-1">Try adjusting your search or category filter</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4 text-xs"
            onClick={() => {
              setSearch('');
              setCategoryFilter('all');
            }}
          >
            Clear Filters
          </Button>
        </div>
      )}
    </div>
  );
}
